import ShipType from "@/model/ShipType"
import Ship from "@/model/Ship"
import Configuration from "@/model/Configuration"
import { propulsionTypeCodes, shipTypeCodes, iceTypeCodes, powerLimitTypeCodes } from "@/utils/constants"
import MainEngineType from "@/model/MainEngineType"
import AuxEngineType from "@/model/AuxEngineType"
import ShipPartType from "@/model/ShipPartType"
import MainEngineEnergySavingsType from "@/model/MainEngineEnergySavingsType"
import AuxEngineEnergySavingsType from "@/model/AuxEngineEnergySavingsType"
import CalcRequiredEEXIService from "@/services/CalcRequiredEEXIService"

class CalcAttainedEEXIService {
    public static calcTotalAttainedValue(configuration: Configuration, ship: Ship, parametricTables) {
        console.log("CalcAttainedEEXIValue. Data", configuration, ship)

        const logElement = new Array<string>()
        configuration.logElement = logElement

        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. Start", logElement)

        const gt_to_use =
            configuration.modified_gt && configuration.modified_gt != 0 ? configuration.modified_gt : ship.gt
        const dwt_to_use =
            configuration.modified_dwt && configuration.modified_dwt != 0 ? configuration.modified_dwt : ship.dwt

        CalcAttainedEEXIService.addToLog(
            "CalcAttainedEEXIValue. gt_to_use = " + gt_to_use + ". dwt_to_use = " + dwt_to_use,
            logElement
        )

        CalcAttainedEEXIService.calc_capacity(ship, gt_to_use, dwt_to_use, logElement)
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue capacity = " + ship.capacity, logElement)

        let mppMotor = 0
        let pme: number = 0
        configuration.MainEngineEmissions.forEach((main_engine) => {
            pme += main_engine.pme
            mppMotor += main_engine.powerLimitValue
        })
        configuration.AuxiliaryEngineEmissions.forEach((auxEngine) => {
            if (auxEngine.shaftMotor) {
                pme += 0.75 * auxEngine.psmmax * auxEngine.npti
            }
        })
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue pme = " + pme + ". mppMotor = " + mppMotor, logElement)
        CalcAttainedEEXIService.calcVRef(ship, configuration, pme, mppMotor, gt_to_use, dwt_to_use, logElement)
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue vref = " + configuration.vref, logElement)

        CalcAttainedEEXIService.calc_fj(configuration, ship, gt_to_use, dwt_to_use, logElement)
        CalcAttainedEEXIService.calc_fw(configuration, logElement)
        CalcAttainedEEXIService.calc_fi(configuration, ship, parametricTables, gt_to_use, dwt_to_use, logElement)
        CalcAttainedEEXIService.calc_fc(configuration, ship, gt_to_use, dwt_to_use, logElement)
        CalcAttainedEEXIService.calc_fl(configuration, ship, logElement)
        CalcAttainedEEXIService.calc_fm(configuration, ship, logElement)

        const fj = configuration.fj_unapply ? 1 : configuration.fj
        const fw = configuration.fw_unapply ? 1 : configuration.fw
        const fi = configuration.fi_unapply ? 1 : configuration.fi
        const fc = configuration.fc_unapply ? 1 : configuration.fc
        const fl = configuration.fl_unapply ? 1 : configuration.fl
        const fm = configuration.fm_unapply ? 1 : configuration.fm

        CalcAttainedEEXIService.addToLog(
            "CalcAttainedEEXIValue. Factors: fj = " +
                fj +
                ". fw = " +
                fw +
                ". fi = " +
                fi +
                ". fc = " +
                fc +
                ". fl = " +
                fl +
                ". fm = " +
                fm,
            logElement
        )

        let mainEnginePartValue: number = 0
        let cfme_x_sfcme = 1
        configuration.MainEngineEmissions.forEach((main_engine) => {
            let actualValue = 0
            if (main_engine.propulsionType === propulsionTypeCodes.Dual) {
                cfme_x_sfcme *= main_engine.cfme
                actualValue = main_engine.pme * main_engine.cfme
            } else {
                cfme_x_sfcme *= main_engine.cfme * main_engine.sfcme
                actualValue = main_engine.pme * main_engine.cfme * main_engine.sfcme
            }

            mainEnginePartValue += actualValue
            CalcAttainedEEXIService.addToLog(
                "CalcAttainedEEXIValue Actual MainEnginePartValue " +
                    main_engine.name +
                    ". value = " +
                    actualValue +
                    ". cfme_x_sfcme = " +
                    main_engine.cfme * main_engine.sfcme,
                logElement
            )
        })
        mainEnginePartValue *= fj
        CalcAttainedEEXIService.addToLog(
            "CalcAttainedEEXIValue MainEnginePartValue when applied fj = " + mainEnginePartValue,
            logElement
        )

        // Cálculo de la parte del motor auxiliar
        let auxEnginePartValue: number = 0
        let ppti = 0
        let cfae_x_sfcae = 1
        if (configuration.AuxiliaryEngineEmissions) {
            configuration.AuxiliaryEngineEmissions.forEach((aux_engine) => {
                cfae_x_sfcae *= aux_engine.cfae * aux_engine.sfcae
                const actualValue = aux_engine.pae * aux_engine.cfae * aux_engine.sfcae
                ppti += aux_engine.ppti

                auxEnginePartValue += actualValue
                CalcAttainedEEXIService.addToLog(
                    "CalcAttainedEEXIValue Actual AuxEnginePartValue " +
                        aux_engine.name +
                        ". value = " +
                        actualValue +
                        ". ppti = " +
                        aux_engine.ppti +
                        ". cfae_x_sfcae = " +
                        aux_engine.cfae * aux_engine.sfcae,
                    logElement
                )
            })
        }
        CalcAttainedEEXIService.addToLog(
            "CalcAttainedEEXIValue TOTAL AuxEnginePartValue value = " +
                auxEnginePartValue +
                ". ppti = " +
                ppti +
                ". cfae_x_sfcae = " +
                cfae_x_sfcae,
            logElement
        )

        // Cálculo de la parte de las medidas innovadoras del motor auxiliar
        let sumatory_feffpaeeff = 0
        if (configuration.AuxiliaryEngineEnergySavings) {
            configuration.AuxiliaryEngineEnergySavings.forEach((auxEngineEnergySaving) => {
                CalcAttainedEEXIService.addToLog(
                    "CalcAttainedEEXIValue.AuxiliaryEngineEnergySavings " +
                        auxEngineEnergySaving.type +
                        ". feffpaeeff = " +
                        auxEngineEnergySaving.feffpaeeff,
                    logElement
                )
                sumatory_feffpaeeff += Number(auxEngineEnergySaving.feffpaeeff)
            })
        }
        const auxEngineEnergySavingsPartValue = (fj * ppti - sumatory_feffpaeeff) * cfae_x_sfcae
        CalcAttainedEEXIService.addToLog(
            "CalcAttainedEEXIValue TOTAL AuxiliaryEngineEnergySavings value = " +
                auxEngineEnergySavingsPartValue +
                ". fj * ppti = " +
                fj * ppti +
                ". sumatory_feffpaeeff = " +
                sumatory_feffpaeeff +
                ". cfae_x_sfcae = " +
                cfae_x_sfcae,
            logElement
        )

        // Cálculo de la parte de las medidas innovadoras del motor principal
        let mainEngineEnergySavingsPartValue = 0
        if (configuration.MainEngineEnergySavings) {
            configuration.MainEngineEnergySavings.forEach((mainEngineEnergySaving) => {
                mainEngineEnergySavingsPartValue += mainEngineEnergySaving.feffpeff * cfme_x_sfcme
            })
        }
        CalcAttainedEEXIService.addToLog(
            "CalcAttainedEEXIValue TOTAL MainEngineEnergySavings value = " + mainEngineEnergySavingsPartValue,
            logElement
        )

        CalcAttainedEEXIService.addToLog(
            "CalcAttainedEEXIValue values to divisor = " +
                fi +
                " - " +
                fc +
                " - " +
                fl +
                " - " +
                fw +
                " - " +
                fm +
                " - " +
                ship.capacity +
                " - " +
                configuration.vref +
                " - " +
                Number(configuration.vref),
            logElement
        )
        const divisor: number = fi * fc * fl * fw * fm * ship.capacity * Number(configuration.vref)
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue divisor = " + divisor, logElement)
        let attainedEEXI: number =
            (mainEnginePartValue +
                auxEnginePartValue +
                auxEngineEnergySavingsPartValue -
                mainEngineEnergySavingsPartValue) /
            divisor

        attainedEEXI = isFinite(attainedEEXI) ? attainedEEXI : 0

        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue attained before round = " + attainedEEXI, logElement)
        configuration.attainedEEXI = Math.round(attainedEEXI * 1000) / 1000
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue Result = " + configuration.attainedEEXI, logElement)

        return configuration.attainedEEXI
    }

    public static calc_capacity(ship: Ship, gt_to_use: number, dwt_to_use: number, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue calc_capacity", logElement)

        let capacity: number = 0
        if (ship.shipType.code === shipTypeCodes.ContainerShips) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue ContainerShip", logElement)
            capacity = dwt_to_use * 0.7
        } else if (ship.shipType.code === shipTypeCodes.Passengers) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue Passengers", logElement)
            capacity = gt_to_use
        } else {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue !Passengers and !ContainerShip ", logElement)
            capacity = dwt_to_use
        }

        ship.capacity = capacity
    }

    public static calcMainEngineEnergySavingsAttainedValue(
        data: MainEngineEnergySavingsType,
        configuration: Configuration
    ) {
        console.log("calcMainEngineEnergySavingsAttainedValue. Data", data)
        const result = {
            ...data
        }

        const logElement = new Array<string>()
        result.logElement = logElement

        CalcAttainedEEXIService.addToLog("calcMainEngineEnergySavingsAttainedValue.", logElement)

        if (data.type === "ALS") {
            CalcAttainedEEXIService.addToLog("calcMainEngineEnergySavingsAttainedValue. ALS", logElement)

            // Hay que obtener el sfcme y el cfme de los motores principales para los cálculos del MainEngineEnergySavings
            let sfcme = 0
            let cfme = 0
            if (configuration.MainEngineEmissions) {
                configuration.MainEngineEmissions.forEach((mainEngine) => {
                    cfme += Number(mainEngine.cfme)
                    sfcme += Number(mainEngine.sfcme)
                })
            }

            // Hay que obtener el sfcae y el cfae de los motores auxiliares para los cálculos del MainEngineEnergySavings
            let sfcae = 0
            let cfae = 0
            if (configuration.AuxiliaryEngineEmissions) {
                configuration.AuxiliaryEngineEmissions.forEach((auxEngine) => {
                    cfae += Number(auxEngine.cfae)
                    sfcae += Number(auxEngine.sfcae)
                })
            }
            CalcAttainedEEXIService.addToLog(
                "calcMainEngineEnergySavingsAttainedValue. ALS. data.paeeffal = " +
                    data.paeeffal +
                    ". cfme = " +
                    cfme +
                    ". sfcme = " +
                    sfcme +
                    ". cfae = " +
                    cfae +
                    ". sfcae = " +
                    sfcae,
                logElement
            )

            result.feff = 1
            result.peff = data.ppeffal - (data.paeeffal * (cfae * sfcae)) / (cfme * sfcme)
            result.feffpeff = result.feff * result.peff
        } else {
            CalcAttainedEEXIService.addToLog("calcMainEngineEnergySavingsAttainedValue. WAPS", logElement)

            result.feff = data.feffpeff
            result.peff = 1
        }

        CalcAttainedEEXIService.addToLog(
            "calcMainEngineEnergySavingsAttainedValue. feff = " +
                result.feff +
                ". peff = " +
                result.peff +
                ". feffpeff = " +
                result.feffpeff,
            logElement
        )

        return result
    }

    public static calcAuxEngineEnergySavingsAttainedValue(data: AuxEngineEnergySavingsType) {
        console.log("calcAuxEngineEnergySavingsAttainedValue. Data", data)
        const result = {
            ...data
        }

        const logElement = new Array<string>()
        result.logElement = logElement

        console.log("calcAuxEngineEnergySavingsAttainedValue.", logElement)

        result.feff = data.feffpaeeff
        result.paeeff = 1

        console.log(
            "calcAuxEngineEnergySavingsAttainedValue. feffpaeeff = " +
                result.feffpaeeff +
                ". paeeff = " +
                result.paeeff,
            logElement
        )

        return result
    }

    public static calcMainEngineAttainedValue(
        parametricTables,
        data: MainEngineType,
        ship: Ship,
        configuration: Configuration
    ) {
        console.log("calcMainEngineAttainedValue. Data", parametricTables, data)
        const result = {
            ...data
        }

        const logElement = new Array<string>()
        result.logElement = logElement

        CalcAttainedEEXIService.addToLog("Vamos a calcular para" + result.name, logElement)

        result.pme = CalcAttainedEEXIService.calc_pme(result, ship.shipType, logElement)
        result.cfme = CalcAttainedEEXIService.calc_cfXe(result, true, parametricTables, logElement)

        let pae = 0
        if (configuration.AuxiliaryEngineEmissions) {
            configuration.AuxiliaryEngineEmissions.forEach((aux_engine) => {
                pae += aux_engine.pae
            })
        }
        CalcAttainedEEXIService.addToLog("calcMainEngineAttainedValue pae " + pae, logElement)
        result.sfcme = CalcAttainedEEXIService.calc_sfcme(result, pae, logElement)

        const ppto = CalcAttainedEEXIService.calc_ppto(result, logElement)
        CalcAttainedEEXIService.calc_shaft_generator(result, pae, ppto, logElement)

        result.pme = Math.round(result.pme * 1000) / 1000
        result.cfme = Math.round(result.cfme * 1000) / 1000
        result.sfcme = Math.round(result.sfcme * 1000) / 1000

        CalcAttainedEEXIService.addToLog(
            "calcMainEngineAttainedValue calculado para " + result.name + ". FIN",
            logElement
        )
        return result
    }

    public static calcAuxEngineAttainedValue(
        parametricTables,
        data: AuxEngineType,
        shipType: ShipType,
        ship_gt: number,
        currentConfiguration: Configuration
    ) {
        console.log("calcAuxEngineAttainedValue. Data", parametricTables, data, shipType, ship_gt)
        const result = {
            ...data
        }

        const gt_to_use = currentConfiguration.modified_gt ? currentConfiguration.modified_gt : ship_gt

        const logElement = new Array<string>()
        result.logElement = logElement

        CalcAttainedEEXIService.addToLog("Vamos a calcular para" + result.name, logElement)

        const { total_pme, total_mcrme, total_sfcme } = CalcAttainedEEXIService.calcMainEnginePartForAuxEngine(
            currentConfiguration.MainEngineEmissions,
            logElement
        )

        result.ppti = CalcAttainedEEXIService.calc_ppti(result, total_mcrme, shipType, logElement)
        result.pae = CalcAttainedEEXIService.calc_pae(
            result,
            logElement,
            shipType,
            gt_to_use,
            total_mcrme,
            total_sfcme,
            total_pme
        )
        result.cfae = CalcAttainedEEXIService.calc_cfXe(result, false, parametricTables, logElement)
        result.sfcae = CalcAttainedEEXIService.calc_sfcae(result, logElement)

        CalcAttainedEEXIService.addToLog(
            "calcAuxEngineAttainedValue calculado para " + result.name + ". FIN",
            logElement
        )
        return result
    }

    private static calcMainEnginePartForAuxEngine(mainEngines: Array<MainEngineType>, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("calcMainEnginePartForAuxEngine", logElement)
        let total_pme = 0
        let total_mcrme_temp = 0
        let total_sfcme = 0
        let usePowerLimitValue = false
        let powerLimitValue = 0

        mainEngines.forEach((engine) => {
            total_mcrme_temp += Number(engine.mcrme)
            total_sfcme += Number(engine.sfcme)
            total_pme += Number(engine.pme)

            usePowerLimitValue ||= engine.powerLimit && engine.powerLimitType === powerLimitTypeCodes.NonOverridableOther
            if (usePowerLimitValue) {
                powerLimitValue = engine.powerLimitValue
                CalcAttainedEEXIService.addToLog(
                    "calcMainEnginePartForAuxEngine. found power limit in " +
                        engine.name +
                        " with limited value " +
                        powerLimitValue,
                    logElement
                )
            }
        })

        const total_mcrme = usePowerLimitValue ? powerLimitValue : total_mcrme_temp
        const result = { total_pme, total_mcrme, total_sfcme }

        CalcAttainedEEXIService.addToLog(
            "calcMainEnginePartForAuxEngine. pme = " +
                total_pme +
                ". mcrme = " +
                total_mcrme +
                ". sfcme = " +
                total_sfcme,
            logElement
        )

        return result
    }

    private static calc_fj(
        configuration: Configuration,
        ship: Ship,
        gt_to_use: number,
        dwt_to_use: number,
        logElement: Array<string>
    ) {
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fj", logElement)

        let fj_ice = 1
        let fj_roro = 1
        let fj_cargo = 1
        if (ship.iceClassType.code !== iceTypeCodes.None) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fj. iceTypeCodes != NONE", logElement)
            const fj0coef = ship.shipType.fj0coef
            const fj0exp = ship.shipType.fj0exp
            const { total_mcrme } = CalcAttainedEEXIService.calcMainEnginePartForAuxEngine(
                configuration.MainEngineEmissions,
                logElement
            )
            const fj0 = (fj0coef * Math.pow(dwt_to_use, fj0exp)) / total_mcrme
            let fjminexp = 1
            let fjmincoef = 1

            if (ship.iceClassType.code === iceTypeCodes.IA) {
                fjminexp = ship.shipType.fjmin1Aexp
                fjmincoef = ship.shipType.fjmin1Acoef
            } else if (ship.iceClassType.code === iceTypeCodes.IB) {
                fjminexp = ship.shipType.fjmin1Bexp
                fjmincoef = ship.shipType.fjmin1Bcoef
            } else if (ship.iceClassType.code === iceTypeCodes.IC) {
                fjminexp = ship.shipType.fjmin1Cexp
                fjmincoef = ship.shipType.fjmin1Ccoef
            } else if (ship.iceClassType.code === iceTypeCodes.IASuper) {
                fjminexp = ship.shipType.fjmin1ASexp
                fjmincoef = ship.shipType.fjmin1AScoef
            }

            const fjmin = fjmincoef * Math.pow(dwt_to_use, fjminexp)
            fj_ice = Math.max(fj0, fjmin)
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fj. fj_ice = " + fj_ice, logElement)

        if (ship.shipType.code === shipTypeCodes.ROPAX || ship.shipType.code === shipTypeCodes.RORO) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fj. fj_roro (RORO o ROPAX) ", logElement)
            const g = 9.8
            const fnl = (0.5144 * configuration.vref) / Math.pow(ship.lpp * g, 1 / 2)
            const shipTypeAlfa = ship.shipType.design_coef_alfa
            const shipTypeBeta = ship.shipType.design_coef_beta
            const shipTypeDelta = ship.shipType.design_coef_delta
            const shipTypeGamma = ship.shipType.design_coef_gamma
            const first_div = Math.pow(fnl, shipTypeAlfa)
            const second_div = Math.pow(ship.lpp / ship.bs, shipTypeBeta)
            const third_div = Math.pow(ship.bs / ship.ds, shipTypeGamma)
            const fourth_div = Math.pow(ship.lpp / Math.pow(ship.displacementVolume, 1 / 3), shipTypeDelta)

            fj_roro = 1 / (first_div * second_div * third_div * fourth_div)
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fj. fj_roro = " + fj_roro, logElement)

        if (ship.shipType.code === shipTypeCodes.GeneralCargoVessels) {
            CalcAttainedEEXIService.addToLog(
                "CalcAttainedEEXIValue. calc_fj. fj_cargo (GeneralCargoVessels)",
                logElement
            )
            const g = 9.8
            let fnv = (0.5144 * configuration.vref) / Math.pow(g * Math.pow(ship.displacementVolume, 1 / 3), 1 / 2)
            fnv = Math.min(fnv, 0.6)
            const first_div = Math.pow(fnv, 2.3)
            const cb = ship.displacementVolume / (ship.lpp * ship.bs * ship.ds)
            const second_div = Math.pow(cb, 0.3)

            fj_cargo = 0.174 / (first_div * second_div)
            fj_cargo = Math.min(fj_cargo, 1)
        } else {
            fj_cargo = 1
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fj. fj_cargo = " + fj_cargo, logElement)

        configuration.fj = fj_ice * fj_roro * fj_cargo
    }

    private static calc_fw(configuration: Configuration, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fw", logElement)
        configuration.fw = 1
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fw. Result = " + configuration.fw, logElement)
    }

    private static calc_fi(
        configuration: Configuration,
        ship: Ship,
        parametricTables,
        gt_to_use: number,
        dwt_to_use: number,
        logElement: Array<string>
    ) {
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fi", logElement)

        let fi_ice = 1
        let fi_vse = 1
        let fi_csr = 1
        if (ship.iceClassType.code !== iceTypeCodes.None && ship.shipType.code !== shipTypeCodes.Passengers) {
            CalcAttainedEEXIService.addToLog(
                "CalcAttainedEEXIValue. calc_fi. fi_ice. ice class " + ship.iceClassType.code + " and no Passengers",
                logElement
            )
            let ficb = 1

            // TODO: Falta mirar Capacity correction factor for ice-classed ships?
            if (
                ship.capacityCorrection &&
                (ship.shipType.code === shipTypeCodes.BulkCarrier ||
                    ship.shipType.code === shipTypeCodes.Tanker ||
                    ship.shipType.code === shipTypeCodes.GeneralCargoVessels)
            ) {
                CalcAttainedEEXIService.addToLog(
                    "CalcAttainedEEXIValue. calc_fi. fi_ice. BulkCarrier or Tanker or GeneralCargoVessels",
                    logElement
                )
                const cbreference = CalcAttainedEEXIService.get_cb_reference(ship.shipType, dwt_to_use)
                const cb = ship.cb
                ficb = cbreference / cb
                ficb = Math.max(ficb, 1)
            }

            const fisum = ship.iceClassType.fisum
            const finum = ship.iceClassType.finum
            CalcAttainedEEXIService.addToLog(
                "CalcAttainedEEXIValue. calc_fi. fi_ice. fisum = " +
                    fisum +
                    ". finum = " +
                    finum +
                    ". dwt = " +
                    dwt_to_use +
                    ". ficb = " +
                    ficb,
                logElement
            )
            fi_ice = (fisum + finum / dwt_to_use) * ficb
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fi. fi_ice = " + fi_ice, logElement)

        if (ship.has_vse) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fi. fi_vse. Has vse", logElement)
            const displacement = ship.displacementVolume
            const lwt_reference_design = ship.lwtReferenceDesign
            const lwt_enhanced_design = ship.lwtEnhancedDesign
            const dwt_reference_design = displacement - lwt_reference_design
            const dwt_enhanced_design = displacement - lwt_enhanced_design
            fi_vse = dwt_reference_design / dwt_enhanced_design
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fi. fi_vse = " + fi_vse, logElement)

        if (ship.has_csr) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fi. has csr ", logElement)
            fi_csr = 1 + 0.08 * (ship.lwtCsr / ship.dwtCsr)
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fi. fi_csr = " + fi_csr, logElement)

        configuration.fi = fi_ice * fi_vse * fi_csr
    }

    private static calc_fc(
        configuration: Configuration,
        ship: Ship,
        gt_to_use: number,
        dwt_to_use: number,
        logElement: Array<string>
    ) {
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fc vTanks=" + ship.vTanks, logElement)
        if (ship.shipType.code === shipTypeCodes.Tanker) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fc Tanker", logElement)
            const r = dwt_to_use / ship.vTanks
            if (r < 0.98) {
                configuration.fc = Math.pow(r, -0.7) - 0.014
            } else {
                configuration.fc = 1
            }
        } else if (ship.shipType.code === shipTypeCodes.LNG) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fc Tanker", logElement)
            const r = dwt_to_use / ship.vTanks
            configuration.fc = Math.pow(r, -0.56)
        } else if (ship.shipType.code === shipTypeCodes.ROPAX) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fc Tanker", logElement)
            if (dwt_to_use / gt_to_use < 0.25) {
                configuration.fc = Math.pow(dwt_to_use / ship.vTanks / 0.25, -0.8)
            } else {
                configuration.fc = 1
            }
        } else if (ship.shipType.code === shipTypeCodes.RORO) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fc Tanker", logElement)
            if (dwt_to_use / gt_to_use < 0.35) {
                configuration.fc = Math.pow(dwt_to_use / ship.vTanks / 0.35, -0.8)
            } else {
                configuration.fc = 1
            }
        } else if (ship.shipType.code === shipTypeCodes.BulkCarrier) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fc Tanker", logElement)
            const r = dwt_to_use / ship.vTanks
            if (r < 0.55) {
                configuration.fc = Math.pow(r, -0.15)
            } else {
                configuration.fc = 1
            }
        } else {
            configuration.fc = 1
        }
    }

    private static calc_fl(configuration: Configuration, ship: Ship, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl", logElement)
        let f_cranes = 1
        if (ship.cranes.length > 0) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl. has_cranes", logElement)
            let sumatory = 0
            ship.cranes.forEach((crane) => (sumatory += 0.0519 * crane.swl * crane.reach + 32.11))

            f_cranes = 1 + sumatory / ship.capacity
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl. f_cranes = " + f_cranes, logElement)

        let f_sideloaders = 1
        if (ship.has_sideloaders) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl. has_sideloaders", logElement)
            f_sideloaders = ship.capacityNoSideloaders / ship.capacitySideloaders
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl. f_sideloaders = " + f_sideloaders, logElement)

        let f_roro = 1
        if (ship.has_roro_ramps) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl. has_roro_ramps", logElement)
            f_roro = ship.capacityNoRORORamp / ship.capacityRORORamp
        }
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl. f_roro = " + f_roro, logElement)

        configuration.fl = f_cranes * f_sideloaders
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fl. Result = " + configuration.fl, logElement)
    }

    private static calc_fm(configuration: Configuration, ship: Ship, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fm", logElement)
        let fm = 1
        if (ship.iceClassType.code === iceTypeCodes.IA || ship.iceClassType.code === iceTypeCodes.IASuper) {
            CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fm. IA or IASuper", logElement)
            fm = 1.05
        }
        configuration.fm = fm
        CalcAttainedEEXIService.addToLog("CalcAttainedEEXIValue. calc_fm. Result = " + configuration.fm, logElement)
    }

    private static get_cb_reference(shipType: ShipType, dwt_to_use: number) {
        let result: number = 0

        if (dwt_to_use <= 10000) {
            result = shipType.cb_ref_10000
        } else if (dwt_to_use <= 25000) {
            result = shipType.cb_ref_25000
        } else if (dwt_to_use <= 55000) {
            result = shipType.cb_ref_55000
        } else if (dwt_to_use <= 75000) {
            result = shipType.cb_ref_75000
        } else {
            result = shipType.cb_ref_MAX
        }

        return result
    }

    private static calc_ppti(data: AuxEngineType, mcrme: number, shipType: ShipType, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("calc_ppti", logElement)
        CalcAttainedEEXIService.addToLog("shipType = " + shipType.code, logElement)
        let result = 0

        if (data.shaftMotor) {
            if (
                shipType.code === shipTypeCodes.Passengers &&
                data.propulsionType === propulsionTypeCodes.DieselElectric
            ) {
                CalcAttainedEEXIService.addToLog("calc_ppti - Pasajeros y Diesel-electric", logElement)
                result = ((0.75 * mcrme) / (data.pigen * data.npti))
            } else if (data.propulsionType === propulsionTypeCodes.SteamTurbine) {
                CalcAttainedEEXIService.addToLog("calc_ppti - STEAM TURBINE", logElement)
                result = ((0.83 * data.psmmax) / (data.pigen * data.npti))
            } else {
                CalcAttainedEEXIService.addToLog("calc_ppti - !STEAM TURBINE", logElement)
                result = ((0.75 * data.psmmax) / (data.pigen * data.npti))
            }
        }
        CalcAttainedEEXIService.addToLog("calc_ppti Result = " + result, logElement)
        return result
    }

    private static calc_pae(
        data: AuxEngineType,
        logElement: Array<string>,
        shipType: ShipType,
        gt_to_use: number,
        total_mcrme: number,
        total_sfcme: number,
        total_pme: number
    ) {
        CalcAttainedEEXIService.addToLog("calc_pae", logElement)
        let result: number

        CalcAttainedEEXIService.addToLog("ShipType = " + shipType.code, logElement)

        if (isFinite(data.hload) && isFinite(data.pigen) && data.hload != 0 && data.pigen != 0) {
            CalcAttainedEEXIService.addToLog("calc_pae tenemos valores de hload y de pigen", logElement)
            result = data.hload / data.pigen
        } else if (shipType.code === shipTypeCodes.Passenger) {
            CalcAttainedEEXIService.addToLog("calc_pae Passenger por aproximación con GT", logElement)
            result = 0.1193 * gt_to_use + 1814.4
        } else if (shipType.code === shipTypeCodes.ROPAX) {
            CalcAttainedEEXIService.addToLog("calc_pae ROPAX por aproximación con GT", logElement)
            result = 0.866 * Math.pow(gt_to_use, 0.732)
        } else {
            CalcAttainedEEXIService.addToLog(
                "calc_pae ni ROPAX ni Passengers. mcrme = " + total_mcrme + ". ppti = " + data.ppti,
                logElement
            )
            if (Number(total_mcrme) + Number(data.ppti) / 0.75 >= 10000) {
                CalcAttainedEEXIService.addToLog("calc_pae >= 10000", logElement)
                result = 0.025 * (total_mcrme + data.ppti / 0.75) + 250
            } else {
                CalcAttainedEEXIService.addToLog("calc_pae < 10000", logElement)
                result = 0.05 * (total_mcrme + data.ppti / 0.75)
            }

            if (shipType.code === shipTypeCodes.LNG && data.reliquefaction) {
                CalcAttainedEEXIService.addToLog("calc_pae LNG y has_relicuefaction", logElement)
                const cargoTankCapacity = data.cargoTankCapacity
                const bor = data.bor / 100
                const cop_cooling = 0.166
                const cop = (425 * 511) / (24 * 3600 * cop_cooling)
                const r = data.bogReliquefy / data.bogTotal
                result += cargoTankCapacity * bor * cop * r
            } else if (shipType.code === shipTypeCodes.LNG && data.highPressureGasCompressor) {
                CalcAttainedEEXIService.addToLog("calc_pae LNG y high_pressure_gas_compressor", logElement)
                const cop_compressor = 0.33
                result += (cop_compressor * total_sfcme * total_pme) / 1000
            } else if (shipType.code === shipTypeCodes.LNG && data.lowPressureGasCompressor) {
                CalcAttainedEEXIService.addToLog("calc_pae LNG y low_pressure_gas_compressor", logElement)
                const factor = 0.02
                result += factor * total_pme
            }
        }

        CalcAttainedEEXIService.addToLog("calc_pae Result = " + result, logElement)
        return result
    }

    private static calc_cfXe(data: ShipPartType, is_pme, parametricTables, logElement) {
        CalcAttainedEEXIService.addToLog("calc_cfXe ", logElement)
        let result: number

        if (
            data.propulsionType === propulsionTypeCodes.Dual ||
            data.propulsionType === propulsionTypeCodes.DieselElectricDual
        ) {
            result = CalcAttainedEEXIService.calc_cf_for_dual(data, is_pme, parametricTables, logElement)
        } else {
            result = CalcAttainedEEXIService.get_value_for_fuel_type("CF", data.fuelType, parametricTables, logElement)
        }

        CalcAttainedEEXIService.addToLog("calc_cfXe Result = " + result, logElement)
        return result
    }

    private static calc_cf_for_dual(data: ShipPartType, is_pme: boolean, parametricTables, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("calc_cf_for_dual ", logElement)
        let result: number

        const vgas = data.vgas
        const kgas = data.kgas
        const sfcgas = data.sfcgas
        const vliquid = data.vliquid
        const kliquid = data.kliquid
        const sfcliquid = data.sfcliquid
        const sfcmepilot = data.sfcmepilot
        const pme = is_pme ? (data as MainEngineType).pme : (data as AuxEngineType).pae

        const lcvliquid = CalcAttainedEEXIService.get_value_for_fuel_type(
            "LCV",
            data.fuelType,
            parametricTables,
            logElement
        )
        const lcvgas = CalcAttainedEEXIService.get_value_for_fuel_type(
            "LCV",
            data.secondFuelType,
            parametricTables,
            logElement
        )

        const pliquid = CalcAttainedEEXIService.get_value_for_fuel_type(
            "density",
            data.fuelType,
            parametricTables,
            logElement
        )
        const pgas = CalcAttainedEEXIService.get_value_for_fuel_type(
            "density",
            data.secondFuelType,
            parametricTables,
            logElement
        )

        const fdfgas =
            (vgas * pgas * lcvgas * kgas) / (vliquid * pliquid * lcvliquid * kliquid + vgas * pgas * lcvgas * kgas)
        const fdfliquid = 1 - fdfgas

        if (fdfgas >= 0.5) {
            CalcAttainedEEXIService.addToLog("fdfgas >= 0.5. Caso normal.", logElement)
            result = CalcAttainedEEXIService.get_value_for_fuel_type("CF", data.fuelType, parametricTables, logElement)
        } else {
            CalcAttainedEEXIService.addToLog("fdfgas < 0.5. Caso especial.", logElement)
            const cfmliquid = CalcAttainedEEXIService.get_value_for_fuel_type(
                "CF",
                data.fuelType,
                parametricTables,
                logElement
            )
            const cfmegas = CalcAttainedEEXIService.get_value_for_fuel_type(
                "CF",
                data.secondFuelType,
                parametricTables,
                logElement
            )

            // Para el cfmepilot se considera siempre que el combustible piloto es diesel (ECOBUQ-66)
            const cfmepilot = CalcAttainedEEXIService.get_value_for_fuel_type("CF", "MDO", parametricTables, logElement)
            result = fdfgas * (cfmepilot * sfcmepilot + cfmegas * sfcgas) + fdfliquid * cfmliquid * sfcliquid
        }

        CalcAttainedEEXIService.addToLog("calc_cf_for_dual Result = " + result, logElement)
        return result
    }

    public static get_value_for_fuel_type(
        value: string,
        fuelType: string,
        parametricTables,
        logElement: Array<string>
    ) {
        let result: number = 0
        parametricTables["fuelType"].forEach((actualFuelType) => {
            if (fuelType === actualFuelType.code) {
                result = actualFuelType[value]
            }
        })

        CalcAttainedEEXIService.addToLog("Value " + value + " for " + fuelType + " = " + result, logElement)

        return result
    }

    private static calc_pme(data: MainEngineType, shipType: ShipType, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("calc_pme ", logElement)
        let result: number
        if (
            data.propulsionType === propulsionTypeCodes.LiquidFuelEngine ||
            data.propulsionType === propulsionTypeCodes.GasEngine ||
            data.propulsionType === propulsionTypeCodes.Dual
        ) {
            CalcAttainedEEXIService.addToLog("calc_pme LFE or GSE, DUL", logElement)

            if (data.powerLimit && data.powerLimitType === powerLimitTypeCodes.Overridable) {
                CalcAttainedEEXIService.addToLog("calc_pme LFE or GSE, DUL. PowerLimit Overridable", logElement)
                result = Math.min(0.83 * data.powerLimitValue, 0.75 * data.mcrme)
            } else if (data.powerLimit && data.powerLimitType !== powerLimitTypeCodes.Overridable) {
                CalcAttainedEEXIService.addToLog("calc_pme LFE or GSE, DUL. PowerLimit !Overridable", logElement)
                result = 0.75 * data.powerLimitValue
            } else {
                CalcAttainedEEXIService.addToLog("calc_pme LFE or GSE, DUL. !PowerLimit", logElement)
                result = 0.75 * data.mcrme
            }
        } else if (
            data.propulsionType === propulsionTypeCodes.DieselElectric ||
            data.propulsionType === propulsionTypeCodes.DieselElectricDual
        ) {
            CalcAttainedEEXIService.addToLog("calc_pme DSE or DED", logElement)

            if (shipType.code === shipTypeCodes.Passengers) {
                CalcAttainedEEXIService.addToLog("calc_pme Passengers", logElement)
                result = 0
            } else if (data.powerLimit && data.powerLimitType === powerLimitTypeCodes.Overridable) {
                CalcAttainedEEXIService.addToLog("calc_pme No Passengers. PowerLimit Overridable", logElement)
                result = (0.83 * data.powerLimitValue) / 0.913
            } else {
                CalcAttainedEEXIService.addToLog("calc_pme No Passengers", logElement)
                result = (0.83 * data.mcrme) / 0.913
            }
        } else {
            CalcAttainedEEXIService.addToLog("calc_pme STM", logElement)
            if (data.powerLimit && data.powerLimitType === powerLimitTypeCodes.Overridable) {
                CalcAttainedEEXIService.addToLog("calc_pme STM. PowerLimit Overridable", logElement)
                result = 0.83 * data.powerLimitValue
            } else {
                CalcAttainedEEXIService.addToLog("calc_pme STM. !PowerLimit or !Overridable", logElement)
                result = 0.83 * data.mcrme
            }
        }
        CalcAttainedEEXIService.addToLog("calc_pme result = " + result, logElement)
        return result
    }

    private static calc_sfcme(data: MainEngineType, pae: number, logElement: Array<string>) {
        let result: number
        CalcAttainedEEXIService.addToLog("calc_sfcme", logElement)

        if (data.propulsionType === propulsionTypeCodes.SteamTurbine) {
            CalcAttainedEEXIService.addToLog("calc_sfcme STM", logElement)
            result = data.fuelConsumption / (data.pme + pae)
        } else {
            CalcAttainedEEXIService.addToLog("calc_sfcme != STM", logElement)
            result = data.sfcme
        }

        CalcAttainedEEXIService.addToLog("calc_sfcme Result = " + result, logElement)
        return result
    }

    private static calc_sfcae(data: AuxEngineType, logElement: Array<string>) {
        CalcAttainedEEXIService.addToLog("calc_sfcae", logElement)

        const result = data.sfcae

        CalcAttainedEEXIService.addToLog("calc_sfcae Result = " + result, logElement)
        return result
    }

    private static calc_shaft_generator(data: MainEngineType, pae: number, ppto: number, logElement) {
        CalcAttainedEEXIService.addToLog("calc_shaft_generator. Actual pme = " + data.pme, logElement)

        if (data.propulsionType === propulsionTypeCodes.DieselElectric || !data.shaftGenerator) {
            CalcAttainedEEXIService.addToLog("No shaft generator", logElement)
        } else {
            let factor: number = 0.75
            if (data.propulsionType === propulsionTypeCodes.SteamTurbine) {
                CalcAttainedEEXIService.addToLog("Shaft Generator (STM)", logElement)
                factor = 0.83
            } else {
                CalcAttainedEEXIService.addToLog("Shaft Generator (LFE, GSE, DUL)", logElement)
            }

            if (factor * ppto <= pae) {
                CalcAttainedEEXIService.addToLog("Shaft Generator factor * ppto <= pae", logElement)
            } else {
                CalcAttainedEEXIService.addToLog("Shaft Generator factor * ppto > pae", logElement)
                ppto = pae / factor
            }

            let mcr = data.mcrme
            if (data.powerLimit && data.powerLimitType === powerLimitTypeCodes.Overridable) {
                CalcAttainedEEXIService.addToLog("Shaft Generator powerLimit Overridable", logElement)
                mcr = data.powerLimitValue
            }

            data.pme = factor * (mcr - ppto)
        }

        CalcAttainedEEXIService.addToLog("calc_shaft_generator. New pme = " + data.pme, logElement)
    }

    private static calc_ppto(data: MainEngineType, logElement: Array<string>) {
        let result: number
        CalcAttainedEEXIService.addToLog("calc_ppto", logElement)
        if (
            data.propulsionType === propulsionTypeCodes.LiquidFuelEngine ||
            data.propulsionType === propulsionTypeCodes.GasEngine ||
            data.propulsionType === propulsionTypeCodes.Dual ||
            data.propulsionType === propulsionTypeCodes.DieselElectricDual
        ) {
            CalcAttainedEEXIService.addToLog("calc_ppto (LFE, GSE, DUL, DED)", logElement)
            result = 0.75 * data.mcrpto
        } else {
            CalcAttainedEEXIService.addToLog("calc_ppto (STM, DSE)", logElement)
            result = 0.83 * data.mcrpto
        }
        CalcAttainedEEXIService.addToLog("calc_ppto Result = " + result, logElement)
        return result
    }

    public static calcVRef(
        ship: Ship,
        configuration: Configuration,
        pme: number,
        mppMotor: number,
        gt_to_use: number,
        dwt_to_use: number,
        logElement: Array<string>
    ) {
        CalcAttainedEEXIService.addToLog("calcVRef ", logElement)
        console.log("calcVRef. Vamos a ver el buque.", ship)

        const pme_or_mppMotor = pme

        let isEEDIResult = false
        if (!configuration.forceVRefCalc) {
            CalcAttainedEEXIService.addToLog("calcVRef no force recalc", logElement)
            isEEDIResult = CalcRequiredEEXIService.isEEDIUsingDates(
                ship.keelLaidDownDate,
                ship.contractSignInDate,
                ship.shipDeliveredDate,
                logElement
            )
        } else {
            CalcAttainedEEXIService.addToLog("calcVRef force recalc", logElement)
        }

        if (isEEDIResult) {
            CalcAttainedEEXIService.addToLog("calcVRef isEEDI", logElement)
            configuration.vref_calc = "Manual"
            configuration.vref = ship.manualVref
        } else {
            CalcAttainedEEXIService.addToLog("calcVRef No isEEDI", logElement)
            if (ship.speedPowerTests) {
                configuration.vref_calc = "Tests"
                configuration.vref = CalcAttainedEEXIService.calcVRefTests(
                    ship,
                    pme_or_mppMotor,
                    gt_to_use,
                    dwt_to_use,
                    logElement
                )
            } else {
                configuration.vref_calc = "Aprox"
                configuration.vref = CalcAttainedEEXIService.calcVRefAprox(
                    ship,
                    pme_or_mppMotor,
                    gt_to_use,
                    dwt_to_use,
                    logElement
                )
            }
        }

        CalcAttainedEEXIService.addToLog("calcVRef vref = " + configuration.vref, logElement)
    }

    public static calcVRefTests(
        ship: Ship,
        pme_or_mppMotor: number,
        gt_to_use: number,
        dwt_to_use: number,
        logElement: Array<string>
    ) {
        CalcAttainedEEXIService.addToLog("calcVRefTests", logElement)
        let result = 0

        if (
            ship.shipType.code === shipTypeCodes.ContainerShips ||
            ship.shipType.code === shipTypeCodes.BulkCarrier ||
            ship.shipType.code === shipTypeCodes.Tanker
        ) {
            CalcAttainedEEXIService.addToLog("calcVRefTests. Container, BulkCarrier o Tanker", logElement)
            let k = 1
            if (ship.shipType.code === shipTypeCodes.ContainerShip) {
                if (dwt_to_use <= 120000) {
                    k = 0.095
                } else {
                    k = 0.093
                }
            } else if (ship.shipType.code === shipTypeCodes.BulkCarrier) {
                if (dwt_to_use <= 200000) {
                    k = 0.097
                }
            } else {
                if (dwt_to_use <= 100000) {
                    k = 0.097
                }
            }

            result =
                Math.pow(k, 1 / 3) *
                Math.pow(ship.dwtsService / ship.capacity, 2 / 9) *
                ship.vsService *
                Math.pow(pme_or_mppMotor / ship.psService, 1 / 3)
        } else {
            CalcAttainedEEXIService.addToLog("calcVRefTests. Ni Container, ni BulkCarrier, ni Tanker", logElement)
            result = ship.vsService * Math.pow(pme_or_mppMotor / ship.psService, 1 / 3)
        }

        return result
    }

    public static calcVRefAprox(
        ship: Ship,
        pme_or_mppMotor: number,
        gt_to_use: number,
        dwt_to_use: number,
        logElement: Array<string>
    ) {
        CalcAttainedEEXIService.addToLog("calcVRefAprox", logElement)

        const a = ship.shipType.A
        const b = CalcRequiredEEXIService.calc_B(ship, gt_to_use, dwt_to_use, logElement)
        const c = ship.shipType.C
        CalcAttainedEEXIService.addToLog("calcVRefAprox. A = " + a + ". B = " + b + ". C = " + c, logElement)

        const vrefavg = a * Math.pow(b, c)
        CalcAttainedEEXIService.addToLog("calcVRefAprox. vrefavg = " + vrefavg, logElement)

        const mv = Math.min(0.05 * vrefavg, 1)
        CalcAttainedEEXIService.addToLog("calcVRefAprox. mv = " + mv, logElement)

        const mcravg = CalcAttainedEEXIService.calc_mcravg(ship, gt_to_use, dwt_to_use, logElement)

        CalcAttainedEEXIService.addToLog(
            "calcVRefAprox. vrefapp = (vrefavg - mv) * Math.pow(pme_or_mppMotor / (0.75 * mcravg), 1 / 3)",
            logElement
        )
        CalcAttainedEEXIService.addToLog(
            "calcVRefAprox. vrefavg = " +
                vrefavg +
                ", mv = " +
                mv +
                ", pme_or_mppMotor = " +
                pme_or_mppMotor +
                ", mcravg = " +
                mcravg,
            logElement
        )
        const vrefapp = (vrefavg - mv) * Math.pow(pme_or_mppMotor / (0.75 * mcravg), 1 / 3)

        CalcAttainedEEXIService.addToLog("calcVRefAprox. vrefapp = " + vrefapp, logElement)

        return vrefapp
    }

    public static calc_mcravg(ship: Ship, gt_to_use: number, dwt_to_use: number, logElement) {
        CalcAttainedEEXIService.addToLog("calc_mcravg", logElement)
        let result: number = 0

        const d = ship.shipType.D
        const e = CalcAttainedEEXIService.calc_E(ship, gt_to_use, dwt_to_use, logElement)
        const f = ship.shipType.F

        result = d * Math.pow(e, f)

        CalcAttainedEEXIService.addToLog("calc_mcravg Result = " + result, logElement)
        return result
    }

    public static calc_E(ship: Ship, gt_to_use: number, dwt_to_use: number, logElement) {
        CalcAttainedEEXIService.addToLog("calc_E", logElement)
        let result: number = 0

        if (ship.shipType.name === "Container ship") {
            result = Math.min(dwt_to_use, 95000)
        } else if (ship.shipType.name === "Passenger vessels") {
            result = gt_to_use
        } else {
            result = dwt_to_use
        }

        CalcAttainedEEXIService.addToLog("calc_E Result = " + result, logElement)
        return result
    }

    private static addToLog(message: string, logElement: Array<string>) {
        logElement.push(message)
        console.log(message)
    }
}

export default CalcAttainedEEXIService
